import { useCallback, useEffect, useRef, useState } from "react";
import { animate, useMotionValue, useReducedMotion } from "framer-motion";

export const wrapPoster = (value, count) => ((value % count) + count) % count;
export const posterDistance = (index, position, count) =>
  count < 2 ? 0 : wrapPoster(index - position + count / 2, count) - count / 2;

export const usePosterCarousel = (count) => {
  const stage = useRef(null);
  const position = useMotionValue(0);
  const spacing = useMotionValue(300);
  const depth = useMotionValue(65);
  const [active, setActive] = useState(0);
  const reduced = useReducedMotion();
  const animation = useRef(null);
  const destination = useRef(0);
  const gesture = useRef(null);
  const suppressClick = useRef(false);

  const settle = useCallback((next) => {
    animation.current?.stop();
    destination.current = Math.round(next);
    setActive(wrapPoster(destination.current, count));
    if (reduced) position.set(destination.current);
    else animation.current = animate(position, destination.current, {
      type: "spring", stiffness: 170, damping: 29, mass: 0.9,
      restDelta: 0.001, restSpeed: 0.01,
    });
  }, [count, position, reduced]);

  const move = useCallback((direction) => {
    if (count > 1) settle(destination.current + direction);
  }, [count, settle]);

  const select = useCallback((index) => {
    settle(destination.current + posterDistance(index, destination.current, count));
  }, [count, settle]);

  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const resize = new ResizeObserver(() => {
      const width = el.querySelector(".poster-artwork")?.offsetWidth;
      if (width) spacing.set(width * (el.clientWidth < 640 ? 0.77 : 0.94));
      depth.set(el.clientWidth < 640 ? 28 : 65);
    });
    resize.observe(el);
    return () => { resize.disconnect(); animation.current?.stop(); };
  }, [spacing, depth]);

  useEffect(() => {
    const el = stage.current;
    let total = 0;
    let lastEvent = 0;
    let lastNavigation = -Infinity;
    const onWheel = (event) => {
      if (event.ctrlKey || count < 2) return;
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
      // Only horizontal intent is consumed. Ordinary wheel scrolling stays on the page.
      if (horizontal) { event.preventDefault(); event.stopPropagation(); }
      const now = performance.now();
      if (now - lastEvent > 180) total = 0;
      lastEvent = now;
      if (now - lastNavigation < 480) return;
      const delta = horizontal && !event.shiftKey ? event.deltaX : event.deltaY;
      total += delta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? el.clientHeight : 1);
      if (Math.abs(total) >= 45) {
        move(Math.sign(total)); total = 0; lastNavigation = now;
      }
    };
    el?.addEventListener("wheel", onWheel, { passive: false });
    return () => el?.removeEventListener("wheel", onWheel);
  }, [count, move]);

  const onPointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0 || count < 2) return;
    animation.current?.stop();
    suppressClick.current = false;
    stage.current.focus({ preventScroll: true });
    gesture.current = {
      id: event.pointerId, x: event.clientX, y: event.clientY,
      start: position.get(), lastX: event.clientX, lastTime: performance.now(), velocity: 0,
      dragging: false,
    };
  };

  const onPointerMove = (event) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId) return;
    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;
    if (!g.dragging) {
      if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
        gesture.current = null; settle(Math.round(position.get())); return;
      }
      if (Math.abs(dx) < 7) return;
      g.dragging = true;
      suppressClick.current = true;
      stage.current.setPointerCapture(event.pointerId);
      stage.current.dataset.dragging = "true";
    }
    const now = performance.now();
    g.velocity = (event.clientX - g.lastX) / Math.max(1, now - g.lastTime);
    g.lastX = event.clientX; g.lastTime = now;
    position.set(g.start - dx / spacing.get());
  };

  const endGesture = (event) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId) return;
    gesture.current = null;
    stage.current.dataset.dragging = "false";
    if (stage.current.hasPointerCapture(event.pointerId)) stage.current.releasePointerCapture(event.pointerId);
    if (!g.dragging) { settle(Math.round(position.get())); return; }
    const velocity = event.type === "pointercancel" || performance.now() - g.lastTime > 100 ? 0 : g.velocity;
    const projection = Math.max(-0.7, Math.min(0.7, velocity * 160 / spacing.get()));
    settle(Math.round(position.get() - projection));
  };

  const onKeyDown = (event) => {
    const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
    if (direction) { event.preventDefault(); move(direction); }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault(); select(event.key === "Home" ? 0 : count - 1);
    }
  };

  return {
    stage, position, spacing, depth, active, reduced, move, select, onKeyDown,
    pointerHandlers: {
      onPointerDown, onPointerMove, onPointerUp: endGesture, onPointerCancel: endGesture,
      onLostPointerCapture: endGesture,
      onClickCapture: (event) => {
        if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); }
      },
    },
  };
};
